import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
} from '@mui/material';
import Seo from '../components/Seo';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Seo
        title="Privacy Policy"
        description="Privacy policies for the InspectIQ internal application and the InspectIQ website (inspectiq.in)."
        path="/privacy-policy"
      />
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Privacy Policy
      </Typography>

      {/* ===== INTERNAL APPLICATION POLICY ===== */}
      <Paper elevation={0} sx={{ p: 4, backgroundColor: 'background.paper', mb: 4 }}>
        <Typography variant="h4" gutterBottom color="primary.main">
          Privacy Policy (Internal Application)
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Last updated: 27/06/2026
        </Typography>
        <Typography paragraph>
          This Privacy Policy applies to the InspectIQ mobile application, which is an internal
          enterprise tool designed strictly for use by authorized InspectIQ inspectors, employees,
          and contractors to conduct and record field inspections.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            1. Information We Collect
          </Typography>
          <Typography paragraph>
            As an internal tool, we collect information necessary to manage your assignments and
            verify field operations. This includes:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography>User credentials (Name, employee/contractor ID, and contact information)</Typography></li>
            <li><Typography>Images and photographs captured via the device camera during field operations (e.g., container, seal, and vehicle inspections)</Typography></li>
            <li><Typography>Location data (GPS coordinates) to verify that inspections are conducted at the assigned client sites and warehouses</Typography></li>
            <li><Typography>Device information, crash logs, and app usage data to ensure application stability and troubleshoot technical issues</Typography></li>
            <li><Typography>Inspection metadata (e.g., cargo details, tally counts, timestamps)</Typography></li>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            2. How We Use Your Information
          </Typography>
          <Typography paragraph>
            We use the information collected through the app solely for internal business operations,
            specifically to:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography>Authenticate authorized personnel and secure access to the app</Typography></li>
            <li><Typography>Manage, assign, and track the progress of field inspections</Typography></li>
            <li><Typography>Generate official inspection reports and provide visual evidence for operational compliance using captured images and location data</Typography></li>
            <li><Typography>Monitor app performance and provide technical support to our inspectors</Typography></li>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            3. Information Sharing
          </Typography>
          <Typography paragraph>
            Because this is an internal application, data is handled strictly within InspectIQ's
            enterprise environment. We do not sell or rent any personal or inspection data. We may
            share data only with:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography>Authorized internal personnel (e.g., managers, report generators)</Typography></li>
            <li><Typography>Essential service providers who assist in our operations (e.g., secure cloud hosting, internal analytics) under strict confidentiality agreements</Typography></li>
            <li><Typography>Law enforcement or regulatory bodies when required by law</Typography></li>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            4. Data Security
          </Typography>
          <Typography paragraph>
            We implement appropriate technical and organizational measures to protect company and
            personnel data against unauthorized access, alteration, disclosure, or destruction.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            5. Your Rights
          </Typography>
          <Typography paragraph>
            As an authorized user, you have the right to:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography>Access your personal profile information within the app</Typography></li>
            <li><Typography>Request corrections to inaccurate profile information via your manager or IT support</Typography></li>
            <li><Typography>Request deletion of your personal account data (Note: Deletion of employment or inspection records may be subject to internal company retention policies and local labor/audit laws)</Typography></li>
            <li><Typography>Withdraw app permissions (e.g., camera or location) via device settings, though this will prevent you from completing assigned inspection tasks</Typography></li>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            6. Data Protection and Infrastructure
          </Typography>
          <Typography paragraph>
            At InspectIQ, we maintain the highest standards of data protection. We operate on a
            closed infrastructure system to ensure maximum security of our clients' and employees'
            data. All information is stored in secure, controlled environments with strict access
            protocols.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" gutterBottom>
            7. Contact Us
          </Typography>
          <Typography paragraph>
            For technical support or questions about data privacy regarding this internal application,
            please contact the IT/Support team at:
          </Typography>
          <Typography paragraph>
            Email: info@inspectiq.in<br />
            Phone: +91 95861 41990
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
            Vadodara Office
          </Typography>
          <Typography>
            Third Floor - 1, Shree Siddheshwar Hallmark,<br />
            Nr, Ajwa Rd, Sayaji Park Society,<br />
            Vadodara, Gujarat 390019<br />
            India
          </Typography>
        </Box>
      </Paper>

      <Divider sx={{ my: 6 }} />

      {/* ===== WEBSITE PRIVACY POLICY ===== */}
      <Paper elevation={0} sx={{ p: 4, backgroundColor: 'background.paper' }}>
        <Typography variant="h4" gutterBottom color="primary.main">
          Website Privacy Policy
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Last updated: 27/06/2026
        </Typography>
        <Typography paragraph>
          This Privacy Policy applies to the InspectIQ website (inspectiq.in) and outlines how we
          collect, use, and protect the data of our clients and website visitors.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            1. Information We Collect
          </Typography>
          <Typography paragraph>
            We collect information that you provide directly to us, including but not limited to:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography>Name and contact information</Typography></li>
            <li><Typography>Company details</Typography></li>
            <li><Typography>Service inquiries and communications</Typography></li>
            <li><Typography>Payment and billing information</Typography></li>
            <li><Typography>Website usage data (e.g., cookies and standard analytics to improve browsing experience)</Typography></li>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            2. How We Use Your Information
          </Typography>
          <Typography paragraph>
            We use the information we collect to:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography>Provide, maintain, and quote our inspection services</Typography></li>
            <li><Typography>Process your requests and commercial transactions</Typography></li>
            <li><Typography>Send you technical notices, updates, and support messages</Typography></li>
            <li><Typography>Communicate with you regarding our services</Typography></li>
            <li><Typography>Improve our website functionality and service offerings</Typography></li>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            3. Information Sharing
          </Typography>
          <Typography paragraph>
            We do not sell or rent your personal information to third parties. We may share your
            information only with:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography>Essential service providers who assist in our operations (e.g., payment processors, secure hosting) under strict confidentiality agreements</Typography></li>
            <li><Typography>Professional advisors and consultants</Typography></li>
            <li><Typography>Law enforcement or regulatory bodies when required by law</Typography></li>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            4. Data Security
          </Typography>
          <Typography paragraph>
            We implement appropriate technical and organizational measures to protect your personal
            information against unauthorized access, alteration, disclosure, or destruction.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            5. Your Rights
          </Typography>
          <Typography paragraph>
            You have the right to:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography>Access your personal information</Typography></li>
            <li><Typography>Correct inaccurate information</Typography></li>
            <li><Typography>Request deletion of your information</Typography></li>
            <li><Typography>Object to the processing of your information</Typography></li>
            <li><Typography>Withdraw consent at any time</Typography></li>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            6. Data Protection and Infrastructure
          </Typography>
          <Typography paragraph>
            At InspectIQ, we maintain the highest standards of data protection. We operate on a
            closed infrastructure system to ensure maximum security of our clients' data. We never
            sell client data to third parties, and it is only shared with essential operational
            partners as outlined in Section 3. All information is stored in secure, controlled
            environments with strict access protocols.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h5" gutterBottom>
            7. Contact Us
          </Typography>
          <Typography paragraph>
            If you have any questions about this Privacy Policy, please contact us at:
          </Typography>
          <Typography paragraph>
            Email: info@inspectiq.in<br />
            Phone: +91 95861 41990
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
            Vadodara Office
          </Typography>
          <Typography>
            Third Floor - 1, Shree Siddheshwar Hallmark,<br />
            Nr, Ajwa Rd, Sayaji Park Society,<br />
            Vadodara, Gujarat 390019<br />
            India
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
